import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  LogOut,
  Monitor,
  Moon,
  Sun,
  LayoutDashboard,
  Building2,
  Briefcase,
  Users,
  Calendar,
  CalendarDays,
  Network,
  IdCard,
  Clock,
  Plane,
  CheckCircle,
  Globe,
  MapPin,
  PartyPopper,
  Lock,
  KeyRound,
  Webhook,
  HardDrive,
  Settings,
  History,
  GitBranch,
  UserCog,
  ChevronDown,
  VenetianMask,
  BarChart3,
  Upload,
  FolderKey,
  Unlink,
  FolderKanban,
} from 'lucide-react';

import { useAuth } from './hooks/useAuth.js';
import { useTheme } from './hooks/useTheme.js';
import { useIsMobile } from './hooks/useIsMobile.js';
import { useViewRoute } from './hooks/useViewRoute.js';
import MobileApp from './mobile/MobileApp.jsx';
import MyProfileButton from './components/MyProfileButton.jsx';
import AppSwitcherMenu from './components/AppSwitcherMenu.jsx';
import { readViewIdFromUrl } from './components/SavedViewsControl.jsx';
import { apiSavedViews, apiPersons } from './api/client.js';
import { applySessionFilter } from './hooks/useFilterStore.js';
import { parseDeepLink, resolvePersonIds, buildPersonCondition } from './lib/deepLinkFilter.js';
import LoginView from './views/LoginView.jsx';
import { IS_NATIVE } from './native/platform.js';
import { NativeSessionError } from './native/NativeScreens.jsx';
import AccountGateView from './views/AccountGateView.jsx';
import DashboardView from './views/DashboardView.jsx';
import DashboardDetailView from './views/DashboardDetailView.jsx';
import ScheduleView from './views/ScheduleView.jsx';
import ClientsView from './views/ClientsView.jsx';
import AffairesView from './views/AffairesView.jsx';
import PersonsView from './views/PersonsView.jsx';
import DepartmentsView from './views/DepartmentsView.jsx';
import RolesView from './views/RolesView.jsx';
import TimeOffTypesView from './views/TimeOffTypesView.jsx';
import CountriesView from './views/CountriesView.jsx';
import RegionsView from './views/RegionsView.jsx';
import PublicHolidaysView from './views/PublicHolidaysView.jsx';
import LogtimeView from './views/LogtimeView.jsx';
import LogTimeLockView from './views/LogTimeLockView.jsx';
import OAuthClientsView from './views/OAuthClientsView.jsx';
import WebhooksView from './views/WebhooksView.jsx';
import SettingsView from './views/SettingsView.jsx';
import UsersView from './views/UsersView.jsx';
import MyTimeOffsView from './views/MyTimeOffsView.jsx';
import TimeOffValidationView from './views/TimeOffValidationView.jsx';
import AuditView from './views/AuditView.jsx';
import BackupsView from './views/BackupsView.jsx';
import HierarchyView from './views/HierarchyView.jsx';
import ReportsView from './views/ReportsView.jsx';
import ProjectsReportView from './views/ProjectsReportView.jsx';
import GescomExportView from './views/GescomExportView.jsx';
import InternalAffairesView from './views/InternalAffairesView.jsx';
import GescomArbitrageView from './views/GescomArbitrageView.jsx';

// Onglets affichés en clair dans le header (accessibles à tout ACTIVE,
// sauf `adminOnly: true`).
// Définitions de vues individuelles (composées ensuite en pills / menus).
/**
 * Ticket #37 — scope de filtre → écran qui porte la `FilterBar` correspondante.
 * Sert à ouvrir le bon onglet quand on arrive par un lien `?view=<id>`.
 */
const VIEW_KEY_BY_FILTER_SCOPE = {
  schedule: 'schedule',
  affaires: 'affaires',
  persons: 'persons',
};

const V_HOME = { key: 'home', label: 'Accueil', Icon: LayoutDashboard, Component: DashboardView };
/**
 * Ticket #49 — écran de détail d'une tuile d'accueil. Hors barre de navigation
 * (on n'y arrive qu'en cliquant une carte), mais bien enregistré comme vue :
 * il a son slug `/home-detail`, la tuile ciblée étant portée par `?metric=`
 * pour qu'un CTRL+R rouvre le même détail (ticket #39).
 */
const V_HOME_DETAIL = {
  key: 'home-detail',
  label: 'Détail accueil',
  Icon: LayoutDashboard,
  Component: DashboardDetailView,
};
const V_SCHEDULE = { key: 'schedule', label: 'Planning', Icon: CalendarDays, Component: ScheduleView };
const V_LOGTIME = { key: 'logtime', label: 'Saisie des temps', Icon: Clock, Component: LogtimeView };
// Reports & Analytics — visible ADMIN + MANAGER. Placé juste après Logtime.
// synotis-support#13 : renommé « Activité » dans le menu « Rapports » ; la clé
// `reports` (slug `/reports`, drill-down Accueil → Rapports) est inchangée.
const V_REPORTS = {
  key: 'reports',
  label: 'Activité',
  Icon: BarChart3,
  Component: ReportsView,
  adminOnly: true,
  managerOk: true,
};
// synotis-support#13 — suivi des projets : visible dès qu'au moins une affaire
// est pilotable (niveau `delegated` de #108, drapeau `canManageAffaires`).
const V_PROJECTS_REPORT = {
  key: 'reports-projects',
  label: 'Projets',
  Icon: FolderKanban,
  Component: ProjectsReportView,
  flag: 'canManageAffaires',
};
const REPORTS_VIEWS = [V_REPORTS, V_PROJECTS_REPORT];
// Sprint 14 — Organigramme managérial. Visible ADMIN + MANAGER.
const V_HIERARCHY = {
  key: 'hierarchy',
  label: 'Hiérarchie',
  Icon: GitBranch,
  Component: HierarchyView,
  adminOnly: true,
  managerOk: true,
};
// #108 — Affaires, affaires internes et clients : visibles dès que le
// périmètre de l'utilisateur n'est pas vide (drapeaux de `/auth/me`, calculés
// serveur). Le backend borne chaque liste et chaque action ; le menu ne fait
// que refléter ce périmètre.
const V_AFFAIRES = {
  key: 'affaires',
  label: 'Affaires',
  Icon: Briefcase,
  Component: AffairesView,
  flag: 'hasAffaires',
};
const V_INTERNAL_AFFAIRES = {
  key: 'internal-affaires',
  label: 'Affaires internes',
  Icon: FolderKey,
  Component: InternalAffairesView,
  flag: 'hasInternalAffaires',
};
const V_CLIENTS = {
  key: 'clients',
  label: 'Clients',
  Icon: Building2,
  Component: ClientsView,
  flag: 'hasClients',
};
const AFFAIRES_VIEWS = [V_AFFAIRES, V_INTERNAL_AFFAIRES, V_CLIENTS];

/**
 * Vue visible par l'utilisateur ? `flag` (#108) : ADMIN ou drapeau de
 * `/auth/me` ; sinon `adminOnly` / `managerOk` historiques.
 */
function canSeeView(v, { user, isAdmin, isManager }) {
  if (!v) return false;
  if (v.flag) return isAdmin || Boolean(user?.[v.flag]);
  if (!v.adminOnly) return true;
  return isAdmin || (Boolean(v.managerOk) && isManager);
}
const V_MY_TIMEOFFS = { key: 'my-timeoffs', label: 'Mes congés', Icon: Plane, Component: MyTimeOffsView };
// Sprint 9 — Validation des congés. Visible ADMIN + MANAGER (backend scopé).
const V_TIMEOFF_VALIDATION = {
  key: 'timeoff-validation',
  label: 'Validation congés',
  Icon: CheckCircle,
  Component: TimeOffValidationView,
  adminOnly: true,
  managerOk: true,
};

// Sous-menu « Congés » regroupant la demande perso et la validation.
const CONGES_VIEWS = [V_MY_TIMEOFFS, V_TIMEOFF_VALIDATION];

// Barre de navigation principale : entrées plates (`view`) ou menus
// déroulants (`menu`). L'ordre ici = l'ordre affiché dans le header.
const NAV_ITEMS = [
  { type: 'view', view: V_HOME },
  { type: 'view', view: V_SCHEDULE },
  { type: 'view', view: V_LOGTIME },
  { type: 'menu', key: 'reports-menu', label: 'Rapports', Icon: BarChart3, views: REPORTS_VIEWS },
  { type: 'menu', key: 'conges', label: 'Congés', Icon: Plane, views: CONGES_VIEWS },
  { type: 'menu', key: 'affaires-menu', label: 'Affaires', Icon: Briefcase, views: AFFAIRES_VIEWS },
  { type: 'view', view: V_HIERARCHY },
];

// Toutes les vues de premier niveau (pour le routing / la résolution de clé).
const TOP_LEVEL_VIEWS = [
  V_HOME,
  V_HOME_DETAIL,
  V_SCHEDULE,
  V_LOGTIME,
  ...REPORTS_VIEWS,
  V_HIERARCHY,
  ...AFFAIRES_VIEWS,
  ...CONGES_VIEWS,
];

// Vues admin groupées sous le menu déroulant "Admin".
const ADMIN_GROUPS = [
  {
    label: 'Métier',
    views: [
      // #108 — Clients et Affaires sont sortis du menu Admin (menu « Affaires »).
      { key: 'persons', label: 'Personnes', Icon: Users, Component: PersonsView },
    ],
  },
  {
    label: 'Organisation',
    views: [
      { key: 'departments', label: 'Départements', Icon: Network, Component: DepartmentsView },
      { key: 'roles', label: 'Rôles', Icon: IdCard, Component: RolesView },
    ],
  },
  {
    label: 'Référentiels',
    views: [
      { key: 'time-off-types', label: 'Types de congés', Icon: Calendar, Component: TimeOffTypesView },
      { key: 'countries', label: 'Pays', Icon: Globe, Component: CountriesView },
      { key: 'regions', label: 'Régions', Icon: MapPin, Component: RegionsView },
      { key: 'public-holidays', label: 'Jours fériés', Icon: PartyPopper, Component: PublicHolidaysView },
    ],
  },
  {
    label: 'Système',
    views: [
      // Sprint 13.A — Journal + dashboard d'audit (lecture seule). Déplacé dans
      // le menu Admin à la demande utilisateur (n'est donc plus visible au
      // MANAGER, le menu Admin n'étant rendu qu'aux ADMIN).
      { key: 'audit', label: 'Audit', Icon: History, Component: AuditView },
      { key: 'users', label: 'Utilisateurs', Icon: UserCog, Component: UsersView },
      { key: 'settings', label: 'Paramètres', Icon: Settings, Component: SettingsView },
      { key: 'logtime-lock', label: 'Verrou Logtime', Icon: Lock, Component: LogTimeLockView },
      { key: 'oauth-clients', label: 'Clients OAuth', Icon: KeyRound, Component: OAuthClientsView },
      { key: 'webhooks', label: 'Webhooks', Icon: Webhook, Component: WebhooksView },
      { key: 'gescom-export', label: 'Export Gescom', Icon: Upload, Component: GescomExportView, adminOnly: true },
      // #63 — affaires actives sans projet Gescom (lecture seule).
      { key: 'gescom-arbitrage', label: 'Arbitrage Gescom', Icon: Unlink, Component: GescomArbitrageView, adminOnly: true },
      { key: 'backups', label: 'Sauvegardes', Icon: HardDrive, Component: BackupsView, adminOnly: true },
    ],
  },
];

// F-S11-02 (pentest Sprint 11) : toutes les vues sous `ADMIN_GROUPS` sont
// admin only. On force `adminOnly: true` à l'aplatissement pour que le garde-fou
// `isAllowed` (`!currentView.adminOnly || isAdmin`) retombe sur Schedule si un
// non-ADMIN cible une de ces vues (défense en profondeur — le menu Admin n'est
// déjà rendu qu'aux ADMIN, et chaque vue s'auto-garde + backend adminRequired).
const ADMIN_VIEWS = ADMIN_GROUPS.flatMap((g) =>
  g.views.map((v) => ({ ...v, adminOnly: true })),
);
const ALL_VIEWS_BY_KEY = new Map(
  [...TOP_LEVEL_VIEWS, ...ADMIN_VIEWS].map((v) => [v.key, v]),
);

function ThemeToggle({ theme, onCycle }) {
  const Icon = theme === 'light' ? Sun : theme === 'dark' ? Moon : Monitor;
  const label =
    theme === 'light' ? 'Thème clair' : theme === 'dark' ? 'Thème sombre' : 'Thème auto';
  return (
    <button
      type="button"
      onClick={onCycle}
      title={label}
      aria-label={label}
      style={{
        background: 'transparent',
        color: 'var(--text)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-sm)',
        padding: 6,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon size={16} />
    </button>
  );
}

function AdminMenu({ activeView, onSelect }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const activeAdminView = ADMIN_VIEWS.find((v) => v.key === activeView);
  const isAdminActive = Boolean(activeAdminView);

  // Calcule la position absolue du dropdown sous le bouton (utilisé par le
  // portal). Recalculé à l'ouverture + sur resize. Au scroll, on ferme : ça
  // évite de tracker en temps réel et reste cohérent UX (le bouton scrolle
  // avec le header sticky de toute façon).
  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const recompute = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ top: rect.bottom + 6, left: rect.left });
    };
    recompute();
    const onResize = () => recompute();
    const onScroll = () => setOpen(false);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      const inButton = buttonRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inButton && !inDropdown) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: isAdminActive ? 'var(--accent)' : 'transparent',
          color: isAdminActive ? '#fff' : 'var(--text)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-pill)',
          padding: '6px 12px',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <Settings size={14} />
        Admin
        {activeAdminView && (
          <span style={{ opacity: 0.85, fontWeight: 400 }}>· {activeAdminView.label}</span>
        )}
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }} />
      </button>

      {open && anchor &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            style={{
              position: 'fixed',
              top: anchor.top,
              left: anchor.left,
              minWidth: 260,
              background: 'var(--card-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-card)',
              padding: '8px 0',
              zIndex: 2000,
              maxHeight: 'calc(100vh - 80px)',
              overflowY: 'auto',
            }}
          >
            {ADMIN_GROUPS.map((group, idx) => (
              <div key={group.label}>
                {idx > 0 && (
                  <div
                    style={{
                      height: 1,
                      background: 'var(--glass-border)',
                      margin: '6px 0',
                    }}
                  />
                )}
                <div
                  style={{
                    fontSize: 11,
                    textTransform: 'uppercase',
                    letterSpacing: 0.6,
                    color: 'var(--text-muted)',
                    padding: '6px 14px 4px',
                  }}
                >
                  {group.label}
                </div>
                {group.views.map((view) => {
                  const Icon = view.Icon;
                  const isActive = view.key === activeView;
                  return (
                    <button
                      key={view.key}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        onSelect(view.key);
                        setOpen(false);
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        width: '100%',
                        background: isActive ? 'var(--glass)' : 'transparent',
                        color: isActive ? 'var(--accent)' : 'var(--text)',
                        border: 'none',
                        padding: '8px 14px',
                        fontSize: 13,
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontWeight: isActive ? 600 : 400,
                      }}
                    >
                      <Icon size={14} />
                      {view.label}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
}

// Menu déroulant générique de premier niveau (ex. « Congés »). Reprend la
// logique d'ancrage/fermeture de `AdminMenu` mais rend une liste plate de vues.
function NavDropdown({ label, Icon: MenuIcon, views, activeView, onSelect }) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  const activeChild = views.find((v) => v.key === activeView);
  const isActive = Boolean(activeChild);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const recompute = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ top: rect.bottom + 6, left: rect.left });
    };
    recompute();
    const onResize = () => recompute();
    const onScroll = () => setOpen(false);
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickOutside = (e) => {
      const inButton = buttonRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inButton && !inDropdown) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          background: isActive ? 'var(--accent)' : 'transparent',
          color: isActive ? '#fff' : 'var(--text)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-pill)',
          padding: '6px 12px',
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
        }}
      >
        <MenuIcon size={14} />
        {label}
        {activeChild && (
          <span style={{ opacity: 0.85, fontWeight: 400 }}>· {activeChild.label}</span>
        )}
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 120ms' }} />
      </button>

      {open && anchor &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            role="menu"
            style={{
              position: 'fixed',
              top: anchor.top,
              left: anchor.left,
              minWidth: 220,
              background: 'var(--card-bg)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-md)',
              boxShadow: 'var(--shadow-card)',
              padding: '8px 0',
              zIndex: 2000,
            }}
          >
            {views.map((view) => {
              const ItemIcon = view.Icon;
              const itemActive = view.key === activeView;
              return (
                <button
                  key={view.key}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onSelect(view.key);
                    setOpen(false);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    background: itemActive ? 'var(--glass)' : 'transparent',
                    color: itemActive ? 'var(--accent)' : 'var(--text)',
                    border: 'none',
                    padding: '8px 14px',
                    fontSize: 13,
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontWeight: itemActive ? 600 : 400,
                  }}
                >
                  <ItemIcon size={14} />
                  {view.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

export default function App() {
  const {
    user,
    loading,
    authError,
    login,
    logout,
    refresh,
    isAuthenticated,
    startImpersonation,
    stopImpersonation,
    isImpersonating,
  } = useAuth();
  const { theme, cycleTheme } = useTheme();
  const isMobile = useIsMobile();

  const isAdmin = user?.role === 'ADMIN';
  // Sprint 9 — Rôle MANAGER : voit les vues `managerOk` (ex. Validation
  // congés) sans avoir besoin du flag ADMIN.
  const isManager = user?.role === 'MANAGER';

  // Entrées de nav visibles selon le rôle (pills + menus déroulants).
  const visibleNavItems = useMemo(() => {
    const isViewVisible = (v) => canSeeView(v, { user, isAdmin, isManager });
    return NAV_ITEMS.map((item) => {
      if (item.type === 'view') {
        return isViewVisible(item.view) ? item : null;
      }
      const views = item.views.filter(isViewVisible);
      if (!views.length) return null;
      // Ticket #31 — un menu déroulant réduit à une seule vue visible par le
      // filtre de rôle (ex. « Congés » pour un collaborateur, qui ne voit pas
      // « Validation congés ») imposait deux clics pour un choix unique. On le
      // rend alors comme une pill plate, directement cliquable.
      if (views.length === 1) return { type: 'view', view: views[0] };
      return { ...item, views };
    }).filter(Boolean);
  }, [user, isAdmin, isManager]);

  // Ticket #39 — l'écran actif est porté par l'URL (`/schedule`, `/logtime`, …),
  // `/` = Accueil (vue par défaut pour tous les rôles depuis le Sprint 13). Un
  // CTRL+R rouvre donc l'écran courant au lieu de retomber sur l'accueil, et
  // Précédent/Suivant du navigateur fonctionnent.
  const { activeView, navigate: setActiveView } = useViewRoute(ALL_VIEWS_BY_KEY, 'home');

  // Ticket #37 — lien partageable `?view=<id>` : l'app est une SPA sans router,
  // on résout donc ici l'écran cible à partir du scope de la vue. C'est ensuite
  // `SavedViewsControl` (monté dans la `FilterBar` de cet écran) qui applique
  // le filtre et le tri mémorisés. Un scope sans `FilterBar` (logtime,
  // timeoffs) n'ouvre aucun écran — le lien reste sans effet.
  useEffect(() => {
    const id = readViewIdFromUrl();
    if (!id) return;
    let cancelled = false;
    apiSavedViews
      .get(id)
      .then((view) => {
        if (cancelled) return;
        const target = VIEW_KEY_BY_FILTER_SCOPE[view?.scope];
        // `replace` : le lien `?view=` EST déjà l'entrée d'historique courante,
        // on aligne juste le pathname dessus sans en empiler une seconde.
        if (target) setActiveView(target, { replace: true });
      })
      .catch(() => {
        // Vue inaccessible (supprimée, non partagée) — on reste sur l'accueil.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Ticket support #15 — lien profond `?persons=<emails>&date=…` (Contrôle
  // Synolog de Synosight) : filtre de SESSION sur ces personnes, posé une fois
  // l'utilisateur connu (la liste des personnes exige une session). Les
  // e-mails inconnus sont ignorés ; les données affichées restent bornées par
  // les droits des endpoints métier. Cf. lib/deepLinkFilter.js.
  const [deepLink] = useState(() => parseDeepLink(window.location.search));
  const deepLinkAppliedRef = useRef(false);
  useEffect(() => {
    if (!user || !deepLink?.emails.length || deepLinkAppliedRef.current) return;
    deepLinkAppliedRef.current = true;
    apiPersons
      .list('active')
      .then(({ items }) => {
        const condition = buildPersonCondition(resolvePersonIds(deepLink.emails, items));
        if (condition) applySessionFilter('schedule', [condition]);
      })
      .catch(() => {
        // Liste indisponible : l'écran s'ouvre avec le filtre habituel.
      });
  }, [user, deepLink]);

  // Ticket #39 — l'URL est désormais une porte d'entrée : un lien vers une vue
  // admin ouvert par un non-ADMIN (ou un slug d'une vue supprimée) doit remettre
  // l'URL en phase avec l'écran réellement affiché, sinon un CTRL+R suivant
  // reproduirait la même redirection silencieuse. `replace` : la correction ne
  // doit pas créer d'entrée d'historique.
  useEffect(() => {
    if (!user) return;
    const view = ALL_VIEWS_BY_KEY.get(activeView);
    const allowed = canSeeView(view, { user, isAdmin, isManager });
    if (!allowed) setActiveView(TOP_LEVEL_VIEWS[0].key, { replace: true });
  }, [activeView, user, isAdmin, isManager, setActiveView]);

  // Contexte de drill-down remonté depuis un widget Accueil vers la vue Rapports.
  const [reportContext, setReportContext] = useState(null);
  const handleDrillDown = (ctx) => {
    setReportContext(ctx ?? null);
    setActiveView('reports');
  };

  // Ticket #49 — tuile d'accueil cliquée → écran de détail. La tuile et le
  // périmètre voyagent par la query string (`syncUrlToView` la conserve), donc
  // un rechargement ou un lien partagé rouvre le même détail.
  const [detailCtx, setDetailCtx] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return { metric: p.get('metric'), scope: p.get('metricScope') };
  });
  const handleOpenDetail = (metric, scope) => {
    setDetailCtx({ metric, scope });
    const url = new URL(window.location.href);
    url.searchParams.set('metric', metric);
    if (scope) url.searchParams.set('metricScope', scope);
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    setActiveView('home-detail');
  };
  // Retour #49 (gekad) : depuis le détail, un jour est cliquable et doit
  // ouvrir l'écran de saisie/planification **positionné sur ce jour** (et sur
  // la personne concernée) — sinon on retombait sur la semaine courante et il
  // fallait renaviguer à la main.
  // Ticket support #15 — `&date=` d'un lien profond : même mécanisme, sur
  // l'écran ouvert par le lien.
  const [dayTarget, setDayTarget] = useState(() =>
    deepLink?.date ? { view: activeView, date: deepLink.date, personId: null } : null,
  ); // { view, date, personId }
  const handleOpenDay = (view, date, personId) => {
    setDayTarget({ view, date, personId: personId ?? null });
    setActiveView(view);
  };

  const handleCloseDetail = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('metric');
    url.searchParams.delete('metricScope');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    setActiveView('home');
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--text-muted)',
        }}
      >
        Chargement…
      </div>
    );
  }

  if (!isAuthenticated) {
    // App native : la session est portée par NativeGate. Arriver ici signifie
    // que `/auth/me` a échoué sans 401 (réseau, serveur) — jamais de renvoi
    // vers le login SSO web, qui ne peut pas aboutir dans la WebView.
    if (IS_NATIVE) return <NativeSessionError onRetry={refresh} onLogout={logout} />;
    return <LoginView onLogin={login} authError={authError} />;
  }

  if (user.status !== 'ACTIVE') {
    return <AccountGateView status={user.status} email={user.email} onLogout={logout} />;
  }

  // Expérience mobile dédiée (PWA) : shell + vues mobiles séparées. Les vues
  // desktop ne sont jamais montées sur petit écran.
  if (isMobile) {
    return (
      <MobileApp
        user={user}
        activeView={activeView}
        onSelectView={setActiveView}
        onImpersonate={startImpersonation}
        isImpersonating={isImpersonating}
        onStopImpersonation={stopImpersonation}
        logout={logout}
        theme={theme}
        cycleTheme={cycleTheme}
      />
    );
  }

  const currentView = ALL_VIEWS_BY_KEY.get(activeView) ?? TOP_LEVEL_VIEWS[0];
  // Garde-fou : si un USER tente d'accéder à une vue admin (ex: URL bookmarkée
  // côté futur router), on retombe sur Schedule.
  const isAllowed = canSeeView(currentView, { user, isAdmin, isManager });
  const ActiveComponent = (isAllowed ? currentView : TOP_LEVEL_VIEWS[0]).Component;

  return (
    <div style={{ height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {isImpersonating ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 24px',
            background: 'var(--warning)',
            color: '#1a1a1a',
            fontSize: 13,
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          <VenetianMask size={16} />
          <span>
            Vous êtes connecté en tant que{' '}
            <strong>{user.name || user.email}</strong>
            {user.impersonatedBy?.email ? (
              <span style={{ fontWeight: 500 }}> — session ouverte par {user.impersonatedBy.email}</span>
            ) : null}
          </span>
          <button
            type="button"
            onClick={stopImpersonation}
            style={{
              marginLeft: 'auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: '#1a1a1a',
              color: '#fff',
              border: 'none',
              borderRadius: 'var(--radius-pill)',
              padding: '5px 14px',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            <LogOut size={13} /> Revenir à mon compte
          </button>
        </div>
      ) : null}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: 'var(--nav-bg)',
          borderBottom: '1px solid var(--glass-border)',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <img
          src="/synolog-logo-horizontal.svg"
          alt="Synolog"
          style={{
            height: 40,
            background: '#fff',
            borderRadius: 'var(--radius-sm)',
            padding: '4px 10px',
            boxShadow: 'var(--shadow-card)',
          }}
        />

        <nav style={{ display: 'flex', gap: 8, marginLeft: 24, alignItems: 'center' }}>
          {visibleNavItems.map((item) => {
            if (item.type === 'menu') {
              return (
                <NavDropdown
                  key={item.key}
                  label={item.label}
                  Icon={item.Icon}
                  views={item.views}
                  activeView={currentView.key}
                  onSelect={setActiveView}
                />
              );
            }
            const view = item.view;
            const Icon = view.Icon;
            const isActive = view.key === currentView.key;
            return (
              <button
                key={view.key}
                type="button"
                onClick={() => setActiveView(view.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  background: isActive ? 'var(--accent)' : 'transparent',
                  color: isActive ? '#fff' : 'var(--text)',
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-pill)',
                  padding: '6px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                <Icon size={14} />
                {view.label}
              </button>
            );
          })}
          {isAdmin && <AdminMenu activeView={currentView.key} onSelect={setActiveView} />}
        </nav>

        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <AppSwitcherMenu isAdmin={isAdmin} />
          <ThemeToggle theme={theme} onCycle={cycleTheme} />
          <MyProfileButton />
          <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>{user.email}</span>
          <button
            type="button"
            onClick={logout}
            title="Se déconnecter"
            aria-label="Se déconnecter"
            style={{
              background: 'transparent',
              color: 'var(--text)',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              padding: 6,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <main style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <ActiveComponent
          user={user}
          onImpersonate={startImpersonation}
          onDrillDown={handleDrillDown}
          reportContext={reportContext}
          onOpenDetail={handleOpenDetail}
          onOpenDay={handleOpenDay}
          initialDate={dayTarget?.view === activeView ? dayTarget.date : undefined}
          initialPersonId={
            dayTarget?.view === activeView ? (dayTarget.personId ?? undefined) : undefined
          }
          detailMetric={detailCtx.metric}
          detailScope={detailCtx.scope}
          onNavigate={setActiveView}
          onBack={handleCloseDetail}
        />
      </main>
    </div>
  );
}
